import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { useProposalRfiHistory } from '../../../../pages/RfiResponse/rfiFeedbackHooks';
import { rfiAdoptionLink, rfiFeedbackLink, rfiSubmissionLink } from '../../../../pages/RfiResponse/rfiFeedbackLinks';
const T = Testids.proposalRfiHistory;
/** No mutation controls or hidden-source placeholders belong in this projection. */ export function ProposalRfiHistoryPanel({ proposalProjectId }) {
    const query = useProposalRfiHistory(proposalProjectId);
    const data = query.isError ? undefined : query.data;
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": T.panel,
        "aria-busy": query.isFetching,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    level: 3,
                    size: "md",
                    children: "RFI submission and feedback history"
                }),
                query.isLoading ? /*#__PURE__*/ _jsx("output", {
                    children: "Loading RFI history…"
                }) : query.isError ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            role: "alert",
                            children: "Unable to load RFI history."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>void query.refetch(),
                            children: "Retry history"
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "p",
                            children: [
                                "Readable histories: ",
                                /*#__PURE__*/ _jsx("span", {
                                    "data-testid": T.count,
                                    children: data?.readableCount ?? 0
                                })
                            ]
                        }),
                        data?.histories.map((h)=>/*#__PURE__*/ _jsxs("section", {
                                "data-testid": T.entry,
                                "data-rfi-response-id": h.rfiResponseProjectId,
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraHeading, {
                                        level: 4,
                                        size: "sm",
                                        children: h.title
                                    }),
                                    h.submissions.map((s)=>/*#__PURE__*/ _jsxs("section", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(Link, {
                                                    "data-testid": T.submissionLink,
                                                    to: rfiSubmissionLink(h.rfiResponseProjectId, s.id, s.packageVersionId),
                                                    children: [
                                                        "Actual submission ",
                                                        s.id
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    children: [
                                                        "Package ",
                                                        s.packageId,
                                                        " · immutable version ",
                                                        s.packageVersionId,
                                                        " ·",
                                                        ' ',
                                                        s.submittedAt?.toDate().toLocaleString(),
                                                        " · ",
                                                        s.submittedBy,
                                                        " · ",
                                                        s.recipient
                                                    ]
                                                }),
                                                h.packageVersions.find((v)=>v.id === s.packageVersionId)?.items.map((i)=>/*#__PURE__*/ _jsxs(SpecraText, {
                                                        as: "p",
                                                        children: [
                                                            "Item ",
                                                            i.id,
                                                            " · artifact ",
                                                            i.artifactId,
                                                            " v",
                                                            String(i.artifactVersion),
                                                            " · document ",
                                                            i.documentId,
                                                            " version ",
                                                            i.documentVersionId,
                                                            " · SHA256",
                                                            ' ',
                                                            i.contentSha256
                                                        ]
                                                    }, i.id))
                                            ]
                                        }, s.id)),
                                    h.versions.map((v)=>/*#__PURE__*/ _jsxs("section", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(Link, {
                                                    "data-testid": T.feedbackLink,
                                                    to: rfiFeedbackLink(h.rfiResponseProjectId, v.id),
                                                    children: [
                                                        "Feedback v",
                                                        v.version
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "p",
                                                    children: v.body
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    children: [
                                                        "Submission ",
                                                        v.submissionId,
                                                        " · ",
                                                        v.actorUserId,
                                                        " ·",
                                                        ' ',
                                                        v.createdAt?.toDate().toLocaleString()
                                                    ]
                                                })
                                            ]
                                        }, v.id)),
                                    h.adoptions.map((a)=>/*#__PURE__*/ _jsxs("section", {
                                            children: [
                                                /*#__PURE__*/ _jsxs(Link, {
                                                    "data-testid": T.adoptionLink,
                                                    to: rfiAdoptionLink(a),
                                                    children: [
                                                        "Adopted ",
                                                        a.targetKind,
                                                        " · ",
                                                        a.targetField
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(SpecraText, {
                                                    as: "p",
                                                    children: [
                                                        "Feedback ",
                                                        a.sourceVersionId,
                                                        " · ",
                                                        a.actorUserId,
                                                        " ·",
                                                        ' ',
                                                        a.adoptedAt?.toDate().toLocaleString(),
                                                        " · ",
                                                        a.beforeVersionId,
                                                        " →",
                                                        ' ',
                                                        a.resultVersionId
                                                    ]
                                                })
                                            ]
                                        }, a.id))
                                ]
                            }, h.rfiResponseProjectId))
                    ]
                })
            ]
        })
    });
}
